import styled from '@emotion/styled';
import React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Edit } from '@mui/icons-material';
import SectionCard from './SectionCard';

export const NAME_MAX_LENGTH = 30;
export const DESCRIPTION_MAX_LENGTH = 80;

function FormDialog({ open, handleClose, handleSave, dialogTitle, textLabel, initial, saveLabel, showDescription }) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const nameTooLong = name.length > NAME_MAX_LENGTH;
  const descriptionTooLong = showDescription && description.length > DESCRIPTION_MAX_LENGTH;

  React.useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setDescription(initial?.description || "");
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={textLabel}
          type="text"
          fullWidth
          variant="standard"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameTooLong}
          helperText={`${name.length}/${NAME_MAX_LENGTH}${nameTooLong ? " — please shorten" : ""}`}
          FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
        />
        {showDescription && (
          <TextField
            margin="dense"
            label="Description (optional)"
            type="text"
            fullWidth
            multiline
            minRows={2}
            variant="standard"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={descriptionTooLong}
            helperText={`${description.length}/${DESCRIPTION_MAX_LENGTH}${descriptionTooLong ? " — please shorten" : ""}`}
            FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} size="small">Cancel</Button>
        <Button onClick={() => handleSave(name.trim(), showDescription ? description.trim() : "")} disabled={nameTooLong || descriptionTooLong} variant="contained" size="small">{saveLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

/* List manager for text options with name + description (no images) */
const TextDescriptionListManager = ({
  title,
  choiceLabel,
  textLabel,
  showDescription = true,
  getData,
  addData,
  editData,
  deleteData,
  __snackBarText,
  __snackBarSeverity,
  mainContext,
}) => {
  const { setSnackBar } = React.useContext(mainContext);
  const [dataList, setDataList] = React.useState([]);
  const [dialog, setDialog] = React.useState({ open: false, item: null });

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type]
    });
  };

  const isFailed = (res) => {
    if (!res || res.status === 500) {
      callSnackBar(__snackBarText.internalServerError, "error");
      return true;
    }
    if (res.data.status !== 200) {
      callSnackBar(res.data.errors?.[0]?.message || res.data.message || "Error", "error");
      return true;
    }
    return false;
  };

  const fetchData = async () => {
    const res = await getData();
    if (isFailed(res)) return;
    setDataList(res.data.data.data || []);
  };

  const handleSave = async (name, description) => {
    if (!name) return callSnackBar(`Write ${textLabel.toLowerCase()}`, "warning");
    const editing = dialog.item;
    const res = editing ? await editData(editing._id, name, description) : await addData(name, description);
    if (isFailed(res)) return;
    if (editing) {
      setDataList(dataList.map(item => item._id === editing._id ? { ...item, name, description } : item));
      callSnackBar("Data updated successfully", "success");
    } else {
      setDataList([...dataList, res.data.data]);
      callSnackBar("Data added successfully", "success");
    }
    setDialog({ open: false, item: null });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this option?")) return;
    const res = await deleteData(id);
    if (isFailed(res)) return;
    setDataList(dataList.filter(item => item._id !== id));
    callSnackBar("Data deleted successfully", "success");
  };

  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <FormDialog
        open={dialog.open}
        initial={dialog.item}
        handleClose={() => setDialog({ open: false, item: null })}
        handleSave={handleSave}
        dialogTitle={dialog.item ? `Update ${textLabel.toLowerCase()}` : `Add new ${textLabel.toLowerCase()}`}
        saveLabel={dialog.item ? "Update" : "Add"}
        textLabel={textLabel}
        showDescription={showDescription}
      />
      <SectionCard
        title={title}
        pill={choiceLabel}
        action={<Button variant="contained" size='small' onClick={() => setDialog({ open: true, item: null })}>Add new</Button>}
      >
        <Row showDescription={showDescription} className="head">
          <div>{textLabel}</div>
          {showDescription && <div>Description</div>}
          <div></div>
        </Row>
        {dataList.length === 0 && <Empty>No data found</Empty>}
        {dataList.map((item) => (
          <Row key={item._id} showDescription={showDescription}>
            <div className="name">{item.name}</div>
            {showDescription && <div className="desc">{item.description || "-"}</div>}
            <div className="actions">
              <Edit className="edit-icon" fontSize="small" onClick={() => setDialog({ open: true, item })} />
              <Button size="small" color="error" onClick={() => handleDelete(item._id)}>Delete</Button>
            </div>
          </Row>
        ))}
      </SectionCard>
    </>
  );
};

const Row = styled("div", { shouldForwardProp: (prop) => prop !== "showDescription" })`
  display: grid;
  grid-template-columns: ${({ showDescription }) => showDescription ? "minmax(110px, 1fr) 2fr auto" : "1fr auto"};
  align-items: center;
  column-gap: 1rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid rgba(101, 122, 144, 0.12);
  &:last-of-type{ border-bottom: none; }
  &.head{ font-family: "semi-bold"; font-size: 85%; padding-top: 0.75rem; }
  .desc{ color: #555; font-size: 88%; line-height: 1.4; }
  .actions{ display: flex; align-items: center; gap: 0.25rem; justify-content: flex-end; }
  .edit-icon{ cursor: pointer; color: rgba(101, 122, 144, 1); }
`;
const Empty = styled("div")`
  padding: 0.75rem 0;
  font-size: 88%;
  color: #6b6b6b;
`;

export default TextDescriptionListManager;
